/**
 * usePendingPublicActionResume — orchestrator hook for "public share pending
 * action resume": after an anonymous public-share visitor signs in to
 * complete a write action they attempted pre-sign-in (Approve, Save — see
 * `@discovr/common`'s `pendingPublicAction` storage module), this hook waits
 * for the resumed page's identity (`campaignId`) to load, re-validates that
 * the action is still safe to replay against LIVE server data
 * (`pendingPublicActionRegistry.ts`'s `resolve()`), and surfaces the state
 * needed to ask the user to confirm.
 *
 * ─── Why this hook returns STATE, not JSX ────────────────────────────────
 * The confirm dialog it drives (`PendingActionConfirmDialog`) lives in
 * `@discovr/ui`. Package dependency direction in this monorepo runs
 * `@discovr/ui` → `@discovr/features` (see packages/ui/package.json's
 * `@discovr/features` dependency) — NOT the reverse:
 * `packages/features/package.json` has no `@discovr/ui` entry, and this
 * repo's pnpm node-linker is `hoisted` for regular npm packages but NOT for
 * workspace packages (verified: `@discovr/ui` is not symlinked into
 * `packages/features/node_modules/@discovr/`, unlike `@discovr/common` /
 * `@discovr/api` / `@discovr/types`, which are). Importing the dialog here —
 * even via a dynamic `import()` — would be an unresolvable module at both
 * build time and runtime, on top of creating a circular workspace
 * dependency. So this hook stops at returning the `dialog` state a caller
 * needs; a small presentational "Host" component living in `@discovr/ui`
 * turns that state into a dynamically-imported `<PendingActionConfirmDialog />`.
 *
 * ─── REWORK (final-review fix) — the wait ───────────────────────────────
 * The previous version waited on `useQueries` observers subscribed to cache
 * keys computed once, synchronously, on first render — before the page had
 * populated `useCampaignUiStore.campaignId` (that store write happens in a
 * page effect that fires AFTER the async slug→ObjectID resolve settles,
 * always later than this hook's first render). Worse, a `queryFn`-less
 * `useQueries` observer does NOT sit at `pending` forever in TanStack Query
 * v5 — it fetches on subscribe, finds no `queryFn`, and settles to `error`
 * within about one retry-backoff cycle, regardless of whether real data ever
 * arrives. The wait "finished" almost immediately, every time, against a
 * cold, wrong-key cache. See the final review report for the full chain.
 *
 * This version instead:
 *   1. Subscribes REACTIVELY to `useCampaignUiStore`'s `campaignId` (via the
 *      Zustand selector hook, not `.getState()`) — the effect below re-fires
 *      the moment the page populates it.
 *   2. Once `campaignId` is non-empty, calls `handler.resolve()` — which
 *      owns its own `fetchQuery` calls against the real API (see the
 *      registry) — exactly once per pending action (`settledRef` guards
 *      against a second run, including races with the circuit breaker).
 *   3. Branches on the result exactly as before: invalid → toast + clear;
 *      valid → show the confirm dialog.
 *
 * ─── Circuit breaker ──────────────────────────────────────────────────────
 * Unchanged: a single 10s timer, armed once when a fresh pending action is
 * found (mount-only, not re-armed when `campaignId` changes). Whichever
 * finishes first — `resolve()` settling, or the timeout — wins; `settleOnce`
 * guards the race so the loser is a no-op. If `campaignId` never becomes
 * truthy (e.g. the page never mounts, or never resolves it), `resolve()` is
 * simply never called and the circuit breaker is what ends the wait.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  readPendingPublicAction,
  peekPendingPublicActionExpired,
  clearPendingPublicAction,
  useAuth,
  type PendingPublicAction,
} from '@discovr/common';
import { errorMessage } from '../../copy';
import {
  PENDING_ACTION_HANDLERS,
  type AuthUser,
  type ExecuteCtx,
  type PendingActionHandler,
  type ValidationResult,
} from '../pendingPublicActionRegistry';
import { useCampaignUiStore } from '../state/useCampaignUiStore';
import { useCampaignCapabilitiesStore } from '../state/useCampaignCapabilitiesStore';

const CIRCUIT_BREAKER_TIMEOUT_MS = 10_000;
const TIMEOUT_MESSAGE =
  "We couldn't resume your action — the page didn't load as expected. Please navigate there and try again.";
const EXECUTE_FAILURE_FALLBACK = "We couldn't complete this action — please try again.";
const EXPIRED_ACTION_MESSAGE = 'Your request expired — please try again.';

type Phase = 'idle' | 'waiting' | 'confirming' | 'done';

export interface PendingPublicActionResumeDialogState {
  title: string;
  description: string;
  /** True while the confirmed action's `execute()` is in flight. */
  confirming: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export interface PendingPublicActionResumeState {
  /** Non-null exactly while a validated pending action awaits user confirmation. */
  dialog: PendingPublicActionResumeDialogState | null;
}

/**
 * Runs `run` against the handler+action pair for `action.type`, narrowed via
 * switch so both are correctly correlated to the SAME concrete action
 * subtype — no unsafe casts. A plain `PENDING_ACTION_HANDLERS[action.type]`
 * index can't type-check against `PendingActionHandler<A>`'s per-method
 * generic `A`: TS has no way to prove, structurally, that the handler
 * fetched via a union key actually matches the union-typed `action` passed
 * alongside it. The switch supplies that proof per-branch instead.
 */
function withHandler<R>(
  action: PendingPublicAction,
  run: <A extends PendingPublicAction>(handler: PendingActionHandler<A>, action: A) => R,
): R {
  switch (action.type) {
    case 'save_deliverables':
      return run(PENDING_ACTION_HANDLERS.save_deliverables, action);
    case 'approve_script':
      return run(PENDING_ACTION_HANDLERS.approve_script, action);
    case 'approve_content':
      return run(PENDING_ACTION_HANDLERS.approve_content, action);
    case 'navigate':
      return run(PENDING_ACTION_HANDLERS.navigate, action);
    case 'select_pill':
      return run(PENDING_ACTION_HANDLERS.select_pill, action);
    case 'open_insights':
      return run(PENDING_ACTION_HANDLERS.open_insights, action);
    case 'approve_creator':
      return run(PENDING_ACTION_HANDLERS.approve_creator, action);
    case 'reject':
      return run(PENDING_ACTION_HANDLERS.reject, action);
  }
}

export interface UsePendingPublicActionResumeOptions {
  /**
   * Navigates the resumed session to a tab (and optionally a pill within
   * it) — passed straight through to `ExecuteCtx` for the `navigate` /
   * `select_pill` handlers. This package has no dependency on
   * `@tanstack/react-router` (see this file's own doc comment for the
   * dependency-direction rationale that already governs the dialog import),
   * so the actual router call is built by the caller
   * (`PendingPublicActionResumeHost` in `@discovr/ui`, which does depend on
   * the router) and injected here. Omit in a context with no navigation
   * concept (e.g. a test) — the `navigate`/`select_pill` handlers simply
   * become inert in that case.
   */
  navigateToTab?: (target: { tab: string; pill?: string }) => void;
}

const NOOP_NAVIGATE = () => {
  // No navigation mechanism was supplied to this hook instance — see
  // `UsePendingPublicActionResumeOptions.navigateToTab`'s doc comment.
  //
  // Every resume handler's execute() ends in a call to this (see the
  // registry) — silently no-opping here used to mean onConfirm's try block
  // completed without error, so the confirmed action was cleared from
  // storage and the flow marked "done" having done NOTHING the user could
  // see. Throwing routes this into onConfirm's existing catch/finally
  // instead: the user gets EXECUTE_FAILURE_FALLBACK's toast instead of
  // silent success. Always the last call in every handler's execute() (no
  // writes happen after it — see the registry), so throwing here is safe:
  // there's nothing downstream this could leave half-done.
  throw new Error(
    '[usePendingPublicActionResume] navigateToTab was not supplied — cannot complete a resumed navigation.',
  );
};

export function usePendingPublicActionResume(
  options: UsePendingPublicActionResumeOptions = {},
): PendingPublicActionResumeState {
  const navigateToTab = options.navigateToTab ?? NOOP_NAVIGATE;
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // The registry's handlers were built against a minimal `{ uid: string }`
  // identity, not Firebase's `User` — @discovr/features has no direct
  // dependency on firebase/auth (see pendingPublicActionRegistry.ts's
  // `AuthUser` doc comment). Handlers treat a falsy `currentUser` as
  // "not signed in" (`if (!ctx.currentUser)`) despite the type saying
  // `AuthUser` rather than `AuthUser | null` — the registry's own tests
  // establish this exact cast (`null as unknown as AuthUser`) for that case.
  // Memoized on `user` so this doesn't create a brand-new object identity on
  // every render — it's an effect dependency below.
  const currentUser = useMemo(
    () => (user ? { uid: user.uid } : null) as unknown as AuthUser,
    [user],
  );

  // Reactive subscription — re-renders (and re-fires the resume effect
  // below) the moment the page populates this, unlike the old `.getState()`
  // snapshot which read it exactly once, on first render, before the page
  // had a chance to set it.
  const campaignId = useCampaignUiStore((s) => s.campaignId);
  // Same reactive-wait pattern, for the SAME reason, one hop further down:
  // `campaignId` and `capabilities` (role/permissions/accessLevel) come from
  // two separate, sequential fetches on the resumed page — see
  // capabilitiesReady's own doc comment on useCampaignCapabilitiesStore.
  // Every handler's resolve() reads `accessLevel`/`permissions` via a
  // synchronous `useCampaignCapabilitiesStore.getState()` snapshot (see
  // pendingPublicActionRegistry.ts's saveDeliverablesHandler /
  // approve_script / approve_content / approve_creator / reject) — without
  // this second wait, resolve() used to fire the instant campaignId became
  // non-empty, reading accessLevel/permissions while they were STILL at
  // their mount-time `null` defaults (indistinguishable from "genuinely no
  // access"), so every one of those five resumed actions failed closed with
  // "You don't have permission to do this" for every visitor, regardless of
  // their real access.
  const capabilitiesReady = useCampaignCapabilitiesStore((s) => s.capabilitiesReady);

  // Synchronous, side-effect-free read on first render — never inside an
  // effect. `readPendingPublicAction()` handles expiry internally (silently
  // clears + returns null for a stored action older than
  // PENDING_ACTION_EXPIRY_MS), so on its own an expired action would be
  // indistinguishable from "no pending action was ever set" — violating the
  // design spec's "validation failure is never silent" rule. To tell the two
  // apart, `peekPendingPublicActionExpired()` (non-mutating) is called FIRST,
  // synchronously before the destructive read, so nothing can race between
  // them. `wasExpired` is only true when the peek found an expired action AND
  // the subsequent read came back empty (i.e. it really was that action being
  // cleared, not some other outcome).
  const [{ pendingAction, wasExpired }] = useState<{
    pendingAction: PendingPublicAction | null;
    wasExpired: boolean;
  }>(() => {
    const expiredAtPeek = peekPendingPublicActionExpired();
    const action = readPendingPublicAction();
    return { pendingAction: action, wasExpired: expiredAtPeek && !action };
  });
  const [phase, setPhase] = useState<Phase>(() => (pendingAction ? 'waiting' : 'idle'));
  const [dialogCopy, setDialogCopy] = useState<{ title: string; description: string } | null>(null);
  const [confirming, setConfirming] = useState(false);

  const settledRef = useRef(false);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set alongside `dialogCopy` when `resolve()` succeeds — carried forward
  // (in-memory, not the QueryClient cache) to `execute()` on confirm. See
  // pendingPublicActionRegistry.ts's "cache-eviction fix" doc comment: this
  // is what lets execute() stop re-reading a QueryClient entry that could
  // have been garbage-collected (gcTime 5min) by the time the user confirms.
  const resolvedEntryIdRef = useRef<string | null>(null);

  /** Runs `fn` at most once for this resume attempt — guards the race
   *  between the circuit breaker and the wait actually settling. */
  const settleOnce = useCallback((fn: () => void) => {
    if (settledRef.current) return;
    settledRef.current = true;
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    fn();
  }, []);

  // Expired-action toast — fires once, only when the lazy initializer above
  // determined a pending action WAS present but had already expired (and was
  // silently discarded by readPendingPublicAction()'s own internal expiry
  // check). No further cleanup needed here: storage is already cleared, and
  // `phase` is already 'idle' (same terminal state as "no pending action")
  // since `pendingAction` is null in this case — there's no wait to run, no
  // handler to look up.
  useEffect(() => {
    if (!wasExpired) return;
    toast.error(EXPIRED_ACTION_MESSAGE);
    // Intentionally mount-only: `wasExpired` is fixed for the lifetime of
    // this hook instance (computed once in the lazy useState initializer
    // above, same rationale as the circuit breaker effect below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Circuit breaker — armed exactly once, the moment a fresh pending action
  // is found. Never reset/restarted when `campaignId` changes (see the
  // resume effect below) — it covers the ENTIRE wait, from "no campaignId
  // yet" through "resolve() is in flight".
  useEffect(() => {
    if (!pendingAction) return;
    timeoutIdRef.current = setTimeout(() => {
      settleOnce(() => {
        toast.error(TIMEOUT_MESSAGE);
        clearPendingPublicAction();
        setPhase('done');
      });
    }, CIRCUIT_BREAKER_TIMEOUT_MS);
    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    };
    // Intentionally mount-only: `pendingAction` is fixed for the lifetime of
    // this hook instance (read once via the lazy useState initializer above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The resume wait: gated on BOTH `campaignId` and `capabilitiesReady`
  // actually being populated (not stale snapshots — this effect re-fires
  // when either reactive subscription above sees the page set it). Once
  // both are ready, ask the handler to resolve the action against live
  // server data — every handler's own accessLevel/permissions check reads
  // a `.getState()` snapshot at that exact moment, so both must be true
  // BEFORE calling resolve(), not just campaignId (see capabilitiesReady's
  // subscription above for the bug this closes).
  useEffect(() => {
    if (!pendingAction) return;
    if (!campaignId) return;
    if (!capabilitiesReady) return;
    if (settledRef.current) return;

    let cancelled = false;
    void withHandler(pendingAction, (handler, action) =>
      handler.resolve(action, { queryClient, currentUser }),
    ).then((result: ValidationResult) => {
      if (cancelled) return;
      settleOnce(() => {
        if (!result.ok) {
          toast.error(result.message);
          clearPendingPublicAction();
          setPhase('done');
          return;
        }
        // `?? null` — `resolvedEntryId` is optional on `ValidationResult` now
        // (the nav-intent handlers succeed without one; see that type's doc
        // comment), while this ref stays `string | null` for a stable "not
        // set" sentinel.
        resolvedEntryIdRef.current = result.resolvedEntryId ?? null;
        const copy = withHandler(pendingAction, (handler, action) => handler.confirmCopy(action));
        setDialogCopy(copy);
        setPhase('confirming');
      });
    });

    return () => {
      cancelled = true;
    };
  }, [pendingAction, campaignId, capabilitiesReady, queryClient, currentUser, settleOnce]);

  const onConfirm = useCallback(() => {
    if (!pendingAction) return;
    // Only reachable while the dialog is showing, which only happens after
    // the resume effect's success branch set this — see that branch above.
    // `resolvedEntryId` may be null here — the nav-intent handlers
    // (`navigate` / `select_pill`) resolve successfully without one (see
    // `ValidationResult`'s doc comment); only the write handlers
    // (approve/save) actually need it, and they guard for it themselves.
    const resolvedEntryId = resolvedEntryIdRef.current ?? undefined;
    const ctx: ExecuteCtx = { navigateToTab };
    setConfirming(true);
    void (async () => {
      try {
        await withHandler(pendingAction, (handler, action) => handler.execute(action, resolvedEntryId, ctx));
      } catch (err) {
        // No pending-action-specific error UX per the design doc's
        // "no special-casing" rule — surface it exactly the way this app's
        // existing mutation `onError` handlers do (see
        // useBrandCampaignMutations.ts / useAssignProducts.ts):
        // `toast.error(errorMessage(err, fallback))`.
        toast.error(errorMessage(err, EXECUTE_FAILURE_FALLBACK));
      } finally {
        clearPendingPublicAction();
        setConfirming(false);
        setPhase('done');
      }
    })();
  }, [pendingAction, navigateToTab]);

  const onCancel = useCallback(() => {
    // The one legitimately-silent case — no toast.
    clearPendingPublicAction();
    setPhase('done');
  }, []);

  const dialog: PendingPublicActionResumeDialogState | null =
    phase === 'confirming' && dialogCopy
      ? { title: dialogCopy.title, description: dialogCopy.description, confirming, onConfirm, onCancel }
      : null;

  return { dialog };
}
